/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { Bips2, Bips3, Bips4 } from './url_app_mappings';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

// Choose MSO365 Application to use based on window location origin
export const resolveAzureSpaClientId = (origin = window.location.origin) => {
  let id = process.env.REACT_APP_AZURE_SPA_CLIENT_ID_BIPS1 || '';
  if (Bips2.includes(origin)) {
    id = process.env.REACT_APP_AZURE_SPA_CLIENT_ID_BIPS2 || '';
  } else if (Bips3.includes(origin)) {
    id = process.env.REACT_APP_AZURE_SPA_CLIENT_ID_BIPS3 || '';
  } else if (Bips4.includes(origin)) {
    id = process.env.REACT_APP_AZURE_SPA_CLIENT_ID_BIPS4 || '';
  }

  return id;
};

export const msalConfig = {
  auth: {
    clientId: resolveAzureSpaClientId(),
    authority: `https://login.microsoftonline.com/common`,
    redirectUri: window.location.origin,
    navigateToLoginRequestUrl: false
  },
  cache: {
    // Use localStorage to persist authentication across page reloads and new sessions
    cacheLocation: 'localStorage',
    // Keep cookies off unless you specifically need IE11/old Edge support
    storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        // switch (level) {
        //   case LogLevel.Error:
        //     console.error(message);
        //     return;
        //   case LogLevel.Info:
        //     console.info(message);
        //     return;
        //   case LogLevel.Verbose:
        //     console.debug(message);
        //     return;
        //   case LogLevel.Warning:
        //     console.warn(message);
        //     return;
        // }
      }
    }
  }
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  apiSubscription: {
    endpoint: `${process.env.REACT_APP_PROXY}/mo/`,
    scopes: {
      read: [`api://${process.env.REACT_APP_AZURE_NODE_CLIENT_ID}/Mail.Read`],
      write: [`api://${process.env.REACT_APP_AZURE_NODE_CLIENT_ID}/Mail.Send`],
      user: [`api://${process.env.REACT_APP_AZURE_NODE_CLIENT_ID}/User.Read`]
    }
  }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [
    'User.Read',
    'Mail.Read',
    'Mail.Send',
    'Contacts.Read',
    'Mail.ReadWrite',
    'OnlineMeetings.ReadWrite'
  ]
  // scopes: [
  //   ...protectedResources.apiSubscription.scopes.read,
  //   ...protectedResources.apiSubscription.scopes.write,
  //   ...protectedResources.apiSubscription.scopes.user
  // ]
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */

interface IGraphScopes {
  graphMeEndpoint: string;
  messages: string;
  contacts: string;
  deletedItems: string;
  junkemail: string;
  subscriptions: string;
  archives: string;
  mailFolder: string;
}

export const graphConfig: IGraphScopes = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  messages: 'https://graph.microsoft.com/v1.0/me/messages?$top=10',
  contacts: 'https://graph.microsoft.com/v1.0/me/contacts',
  deletedItems: 'https://graph.microsoft.com/v1.0/me/mailFolders/deleteditems',
  junkemail: 'https://graph.microsoft.com/v1.0/me/mailFolders/junkemail',
  archives: 'https://graph.microsoft.com/v1.0/me/mailFolders/archive',
  subscriptions: 'https://graph.microsoft.com/beta/subscriptions',
  mailFolder: 'https://graph.microsoft.com/v1.0/me/mailFolders'
};
